import { AuthAccountType } from './AuthAccountType';

export interface IContactAddressParts {
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export default class ContactUtil {
  // Two-line tile display: second line only.
  public static getCityStateZip = (contact?: IContactAddressParts | null): string => {
    if (!contact) {
      return '-';
    }
    const city = (contact.city ?? '').toString().trim();
    const state = (contact.state ?? '').toString().trim();
    const zip = (contact.zip ?? '').toString().trim();

    const stateZip = [state, zip].filter(Boolean).join(' ');

    return [city, stateZip].filter(Boolean).join(', ') || '-';
  };

  // Single-line display: street + city/state/zip.
  public static getFullAddress = (contact?: IContactAddressParts | null): string => {
    if (!contact) {
      return '-';
    }
    const street = (contact.streetAddress ?? '').toString().trim();
    const cityStateZip = ContactUtil.getCityStateZip(contact);

    return [street, cityStateZip === '-' ? '' : cityStateZip].filter(Boolean).join(', ') || '-';
  };

  public getContactCustomFieldsByType = (
    contactType: ContactType,
    contactCustomFields: IContactCustomFieldResponse[],
    currentContact?: IContactResponse,
    contactId?: number
  ) => {
    if (!contactCustomFields) {
      return [];
    }

    return contactCustomFields
      .filter((customField) => customField.contactType === contactType)
      .filter((customField) => {
        let shouldShowField = false;
        if (customField.isActive) {
          shouldShowField = true;
        }

        if (contactId && currentContact && currentContact.contactCustomFieldValues) {
          shouldShowField = shouldShowField || currentContact.contactCustomFieldValues[customField.id] !== undefined;
        }

        return shouldShowField;
      });
  };

  public getContactSuggestionLabel = (contact: IContactResponse) => {
    const fullAddress = ContactUtil.getFullAddress(contact);
    const addressString = fullAddress === '-' ? '' : fullAddress;

    const email = contact && contact.email;
    const phone = contact && (contact.workPhone || contact.cellPhone);
    let suggestionString = '';

    if (addressString) {
      suggestionString = addressString;
    }

    if (email) {
      suggestionString = `${suggestionString}${suggestionString ? ', ' : ''}${email}`;
    }

    if (phone) {
      suggestionString = `${suggestionString}${suggestionString ? ', ' : ''}${phone}`;
    }

    return contact.name + (suggestionString ? ` (${suggestionString})` : '');
  };

  public getResponsibleContactId = (
    caseContacts: IXerceCaseContactResponse[],
    recipientResponsiblePartyInfo?: IRecipientResponsiblePartyResponse
  ) => {
    if (!!recipientResponsiblePartyInfo && recipientResponsiblePartyInfo.responsibleContactId) {
      return recipientResponsiblePartyInfo.responsibleContactId;
    }

    const billableContacts = caseContacts.filter((caseContact) => caseContact.isBillable);

    if (billableContacts.length) {
      return billableContacts[0].caseContactId;
    } else if (caseContacts.length) {
      return caseContacts[0].caseContactId;
    } else {
      return null;
    }
  };

  public getRecipientIds = (
    caseContacts: IXerceCaseContactResponse[],
    recipientResponsiblePartyInfo?: IRecipientResponsiblePartyResponse
  ) => {
    if (caseContacts.length === 1) {
      return [caseContacts[0].caseContactId];
    }

    if (!!recipientResponsiblePartyInfo) {
      return recipientResponsiblePartyInfo.recipientIds;
    }

    return [];
  };

  public static isPropertyOwnerRoleLabel = (caseRoleLabel: string): boolean =>
    (caseRoleLabel || '').trim().toLowerCase() === 'property owner';

  public static getAccountTypeFromAuth0UserId = (
    auth0UserId: string | null
  ): AuthAccountType | null => {
    if (!auth0UserId) {
      return null;
    }

    if (auth0UserId.startsWith('google-oauth2|')) {
      return AuthAccountType.Google;
    }

    if (auth0UserId.startsWith('auth0|')) {
      return AuthAccountType.Email;
    }

    if (auth0UserId.startsWith('sms|')) {
      return AuthAccountType.SMS;
    }

    if (auth0UserId.startsWith('apple|')) {
      return AuthAccountType.Apple;
    }

    return null;
  };

  public static readonly APPLE_RELAY_STAFF_EMAIL_LABEL = 'Apple relay — notifications only';

  public static isApplePrivateRelayEmail = (email?: string | null): boolean => {
    if (!email) {
      return false;
    }

    return email.trim().toLowerCase().endsWith('@privaterelay.appleid.com');
  };

  public static getStaffEmailDisplay = (email?: string | null): string => {
    if (ContactUtil.isApplePrivateRelayEmail(email)) {
      return ContactUtil.APPLE_RELAY_STAFF_EMAIL_LABEL;
    }

    return email || '';
  };
}
